import type { Transaction } from '@/types/asset';

// Helper function to generate date X days ago
const generateDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const mockTransactions: Transaction[] = [
  // Last 7 days (3 transactions)
  {
    id: '1',
    auditNumber: 1001,
    assetId: '1',
    assetName: 'Dell Latitude 7420 Laptop',
    type: 'addition',
    transactionDate: generateDateDaysAgo(2),
    amount: 3750.00,
    currency: 'GBP',
    status: 'posted',
    description: 'New laptop purchase for Finance department',
    preparedBy: 'John Smith',
    preparedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0156',
    items: [
      {
        id: '1-1',
        assetId: '1',
        assetName: 'Dell Latitude 7420 Laptop',
        amount: 1250.00,
        description: 'Laptop unit for Finance Manager',
      },
      {
        id: '1-2',
        assetId: '2',
        assetName: 'HP EliteBook 840',
        amount: 1200.00,
        description: 'Laptop unit for Finance Analyst',
      },
      {
        id: '1-3',
        assetId: '3',
        assetName: 'Lenovo ThinkPad X1',
        amount: 1300.00,
        description: 'Laptop unit for Senior Accountant',
      },
    ],
  },
  {
    id: '2',
    auditNumber: 1002,
    assetId: '2',
    assetName: 'HP EliteBook 840',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(4),
    amount: 68.76,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation adjustment',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-001',
    items: [
      {
        id: '2-1',
        assetId: '2',
        assetName: 'HP EliteBook 840',
        amount: 22.92,
        description: 'Monthly depreciation',
      },
      {
        id: '2-2',
        assetId: '4',
        assetName: 'Apple iMac 27"',
        amount: 25.00,
        description: 'Monthly depreciation',
      },
      {
        id: '2-3',
        assetId: '5',
        assetName: 'Dell OptiPlex 7090',
        amount: 20.84,
        description: 'Monthly depreciation',
      },
    ],
  },
  {
    id: '3',
    auditNumber: 1003,
    assetId: '3',
    assetName: 'Lenovo ThinkPad X1',
    type: 'revaluation',
    transactionDate: generateDateDaysAgo(6),
    amount: 4500.00,
    currency: 'GBP',
    status: 'approved',
    description: 'Market value adjustment after appraisal',
    preparedBy: 'Mike Myers',
    preparedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'REV-2024-001',
    items: [
      {
        id: '3-1',
        assetId: '3',
        assetName: 'Lenovo ThinkPad X1',
        amount: 1500.00,
        description: 'Market value adjustment',
      },
      {
        id: '3-2',
        assetId: '7',
        assetName: 'MacBook Pro 16"',
        amount: 1800.00,
        description: 'Market value adjustment',
      },
      {
        id: '3-3',
        assetId: '8',
        assetName: 'Dell XPS 15',
        amount: 1200.00,
        description: 'Market value adjustment',
      },
    ],
  },
  
  // Last 8-14 days (2 additional transactions = 5 total in last 14 days)
  {
    id: '4',
    auditNumber: 1004,
    assetId: '16',
    assetName: 'Executive Desk - Oak',
    type: 'addition',
    transactionDate: generateDateDaysAgo(9),
    amount: 3600.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Large executive desk for Management',
    preparedBy: 'David Lee',
    preparedDate: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0215',
    items: [
      {
        id: '4-1',
        assetId: '16',
        assetName: 'Executive Desk - Oak',
        amount: 1200.00,
        description: 'Executive desk for CEO office',
      },
      {
        id: '4-2',
        assetId: '17',
        assetName: 'Herman Miller Aeron Chair',
        amount: 950.00,
        description: 'Executive chair for CEO',
      },
      {
        id: '4-3',
        assetId: '18',
        assetName: 'Filing Cabinet - 4 Drawer',
        amount: 1450.00,
        description: 'Premium filing cabinets (x4)',
      },
    ],
  },
  {
    id: '5',
    auditNumber: 1005,
    assetId: '17',
    assetName: 'Herman Miller Aeron Chair',
    type: 'addition',
    transactionDate: generateDateDaysAgo(12),
    amount: 2850.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Ergonomic office chair for executive',
    preparedBy: 'Emma Wilson',
    preparedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Mike Myers',
    postedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0220',
    items: [
      {
        id: '5-1',
        assetId: '17',
        assetName: 'Herman Miller Aeron Chair',
        amount: 950.00,
        description: 'Chair for CFO',
      },
      {
        id: '5-2',
        assetId: '19',
        assetName: 'Standing Desk - Electric',
        amount: 850.00,
        description: 'Standing desk for CFO',
      },
      {
        id: '5-3',
        assetId: '20',
        assetName: 'Office Chair - Mesh Back',
        amount: 1050.00,
        description: 'Chairs for management team (x3)',
      },
    ],
  },
  
  // Last 15-30 days (16 additional transactions = 21 total in last 30 days)
  {
    id: '6',
    auditNumber: 1006,
    assetId: '7',
    assetName: 'MacBook Pro 16"',
    type: 'addition',
    transactionDate: generateDateDaysAgo(15),
    amount: 9600.00,
    currency: 'GBP',
    status: 'approved',
    description: 'High-performance laptop for Development',
    preparedBy: 'Emma Wilson',
    preparedDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0189',
    items: [
      {
        id: '6-1',
        assetId: '7',
        assetName: 'MacBook Pro 16"',
        amount: 3200.00,
        description: 'MacBook for Lead Developer',
      },
      {
        id: '6-2',
        assetId: '8',
        assetName: 'Dell XPS 15',
        amount: 2500.00,
        description: 'Dell XPS for Senior Developer',
      },
      {
        id: '6-3',
        assetId: '9',
        assetName: 'HP ZBook Studio',
        amount: 3900.00,
        description: 'HP ZBook for Design Team',
      },
    ],
  },
  {
    id: '7',
    auditNumber: 1007,
    assetId: '26',
    assetName: 'Ford Transit Van',
    type: 'revaluation',
    transactionDate: generateDateDaysAgo(17),
    amount: 84000.00,
    currency: 'GBP',
    status: 'rejected',
    description: 'Revaluation rejected - insufficient documentation',
    preparedBy: 'James Wilson',
    preparedDate: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'REV-2024-0245',
    items: [
      {
        id: '7-1',
        assetId: '26',
        assetName: 'Ford Transit Van',
        amount: 28000.00,
        description: 'Revaluation - Van 1',
      },
      {
        id: '7-2',
        assetId: '27',
        assetName: 'Mercedes Sprinter',
        amount: 35000.00,
        description: 'Revaluation - Van 2',
      },
      {
        id: '7-3',
        assetId: '28',
        assetName: 'Volkswagen Caddy',
        amount: 21000.00,
        description: 'Revaluation - Van 3',
      },
    ],
  },
  {
    id: '8',
    auditNumber: 1008,
    assetId: '8',
    assetName: 'Dell XPS 15',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(18),
    amount: 625.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-002',
    items: [
      {
        id: '8-1',
        assetId: '8',
        assetName: 'Dell XPS 15',
        amount: 208.33,
        description: 'Monthly depreciation',
      },
      {
        id: '8-2',
        assetId: '9',
        assetName: 'HP ZBook Studio',
        amount: 187.50,
        description: 'Monthly depreciation',
      },
      {
        id: '8-3',
        assetId: '10',
        assetName: 'Lenovo ThinkCentre M90',
        amount: 229.17,
        description: 'Monthly depreciation',
      },
    ],
  },
  {
    id: '9',
    auditNumber: 1009,
    assetId: '19',
    assetName: 'Standing Desk - Electric',
    type: 'addition',
    transactionDate: generateDateDaysAgo(19),
    amount: 2550.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Ergonomic standing desk for office',
    preparedBy: 'Emma Wilson',
    preparedDate: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Mike Myers',
    postedDate: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0189',
    items: [
      {
        id: '9-1',
        assetId: '19',
        assetName: 'Standing Desk - Electric',
        amount: 850.00,
        description: 'Standing desk for Marketing',
      },
      {
        id: '9-2',
        assetId: '20',
        assetName: 'Office Chair - Mesh Back',
        amount: 450.00,
        description: 'Chair for Marketing',
      },
      {
        id: '9-3',
        assetId: '21',
        assetName: 'Reception Desk',
        amount: 1250.00,
        description: 'Reception desk upgrade',
      },
    ],
  },
  {
    id: '10',
    auditNumber: 1010,
    assetId: '20',
    assetName: 'Office Chair - Mesh Back',
    type: 'addition',
    transactionDate: generateDateDaysAgo(20),
    amount: 1350.00,
    currency: 'GBP',
    status: 'pending',
    description: 'Office chair for marketing department',
    preparedBy: 'Lisa Anderson',
    preparedDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0201',
    items: [
      {
        id: '10-1',
        assetId: '20',
        assetName: 'Office Chair - Mesh Back',
        amount: 450.00,
        description: 'Chair for Marketing Manager',
      },
      {
        id: '10-2',
        assetId: '22',
        assetName: 'Conference Table - 12 Seater',
        amount: 600.00,
        description: 'Meeting room chair set (x2)',
      },
      {
        id: '10-3',
        assetId: '23',
        assetName: 'Whiteboard - Large',
        amount: 300.00,
        description: 'Whiteboard accessories',
      },
    ],
  },
  {
    id: '11',
    auditNumber: 1011,
    assetId: '27',
    assetName: 'Mercedes Sprinter',
    type: 'revaluation',
    transactionDate: generateDateDaysAgo(21),
    amount: 105000.00,
    currency: 'GBP',
    status: 'approved',
    description: 'Annual market value assessment',
    preparedBy: 'Robert Taylor',
    preparedDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'REV-2024-002',
    items: [
      {
        id: '11-1',
        assetId: '27',
        assetName: 'Mercedes Sprinter',
        amount: 35000.00,
        description: 'Market value assessment',
      },
      {
        id: '11-2',
        assetId: '26',
        assetName: 'Ford Transit Van',
        amount: 28000.00,
        description: 'Market value assessment',
      },
      {
        id: '11-3',
        assetId: '29',
        assetName: 'Nissan NV200',
        amount: 42000.00,
        description: 'Market value assessment',
      },
    ],
  },
  {
    id: '12',
    auditNumber: 1012,
    assetId: '4',
    assetName: 'Apple iMac 27"',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(22),
    amount: 375.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-003',
    items: [
      {
        id: '12-1',
        assetId: '4',
        assetName: 'Apple iMac 27"',
        amount: 125.00,
        description: 'Monthly depreciation',
      },
      {
        id: '12-2',
        assetId: '6',
        assetName: 'HP ProDesk 600',
        amount: 74.92,
        description: 'Monthly depreciation',
      },
      {
        id: '12-3',
        assetId: '11',
        assetName: 'Dell Precision 5560',
        amount: 175.08,
        description: 'Monthly depreciation',
      },
    ],
  },
  {
    id: '13',
    auditNumber: 1013,
    assetId: '35',
    assetName: 'Toyota Forklift 3-Ton',
    type: 'addition',
    transactionDate: generateDateDaysAgo(23),
    amount: 37500.00,
    currency: 'GBP',
    status: 'posted',
    description: 'New forklift for warehouse operations',
    preparedBy: 'James Wilson',
    preparedDate: new Date(Date.now() - 23 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Mike Myers',
    postedDate: new Date(Date.now() - 23 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0245',
    items: [
      {
        id: '13-1',
        assetId: '35',
        assetName: 'Toyota Forklift 3-Ton',
        amount: 12500.00,
        description: 'Forklift for Warehouse A',
      },
      {
        id: '13-2',
        assetId: '36',
        assetName: 'Hyster Forklift 2.5-Ton',
        amount: 11000.00,
        description: 'Forklift for Warehouse B',
      },
      {
        id: '13-3',
        assetId: '37',
        assetName: 'Crown Pallet Jack',
        amount: 14000.00,
        description: 'Electric pallet jacks (x2)',
      },
    ],
  },
  {
    id: '14',
    auditNumber: 1014,
    assetId: '18',
    assetName: 'Filing Cabinet - 4 Drawer',
    type: 'addition',
    transactionDate: generateDateDaysAgo(24),
    amount: 960.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Storage cabinet for HR department',
    preparedBy: 'David Lee',
    preparedDate: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0258',
    items: [
      {
        id: '14-1',
        assetId: '18',
        assetName: 'Filing Cabinet - 4 Drawer',
        amount: 320.00,
        description: 'Filing cabinet for HR',
      },
      {
        id: '14-2',
        assetId: '24',
        assetName: 'Bookshelf - 6 Tier',
        amount: 280.00,
        description: 'Bookshelf for HR',
      },
      {
        id: '14-3',
        assetId: '25',
        assetName: 'Storage Cabinet - Metal',
        amount: 360.00,
        description: 'Storage cabinets for HR',
      },
    ],
  },
  {
    id: '15',
    auditNumber: 1015,
    assetId: '5',
    assetName: 'Dell OptiPlex 7090',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(25),
    amount: 500.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-004',
    items: [
      {
        id: '15-1',
        assetId: '5',
        assetName: 'Dell OptiPlex 7090',
        amount: 166.67,
        description: 'Monthly depreciation',
      },
      {
        id: '15-2',
        assetId: '12',
        assetName: 'HP EliteDesk 800',
        amount: 158.33,
        description: 'Monthly depreciation',
      },
      {
        id: '15-3',
        assetId: '13',
        assetName: 'Lenovo IdeaCentre',
        amount: 175.00,
        description: 'Monthly depreciation',
      },
    ],
  },
  {
    id: '16',
    auditNumber: 1016,
    assetId: '21',
    assetName: 'Reception Desk',
    type: 'revaluation',
    transactionDate: generateDateDaysAgo(26),
    amount: 5400.00,
    currency: 'GBP',
    status: 'approved',
    description: 'Market value adjustment after refurbishment',
    preparedBy: 'Michael Brown',
    preparedDate: new Date(Date.now() - 26 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Mike Myers',
    postedDate: new Date(Date.now() - 26 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'REV-2024-003',
    items: [
      {
        id: '16-1',
        assetId: '21',
        assetName: 'Reception Desk',
        amount: 1800.00,
        description: 'Reception desk revaluation',
      },
      {
        id: '16-2',
        assetId: '22',
        assetName: 'Conference Table - 12 Seater',
        amount: 2100.00,
        description: 'Conference table revaluation',
      },
      {
        id: '16-3',
        assetId: '16',
        assetName: 'Executive Desk - Oak',
        amount: 1500.00,
        description: 'Executive desk revaluation',
      },
    ],
  },
  {
    id: '17',
    auditNumber: 1017,
    assetId: '6',
    assetName: 'HP ProDesk 600',
    type: 'addition',
    transactionDate: generateDateDaysAgo(27),
    amount: 2697.00,
    currency: 'GBP',
    status: 'posted',
    description: 'Desktop computer for accounting',
    preparedBy: 'Lisa Anderson',
    preparedDate: new Date(Date.now() - 27 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Sarah Johnson',
    postedDate: new Date(Date.now() - 27 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0289',
    items: [
      {
        id: '17-1',
        assetId: '6',
        assetName: 'HP ProDesk 600',
        amount: 899.00,
        description: 'Desktop for Accountant 1',
      },
      {
        id: '17-2',
        assetId: '12',
        assetName: 'HP EliteDesk 800',
        amount: 950.00,
        description: 'Desktop for Accountant 2',
      },
      {
        id: '17-3',
        assetId: '13',
        assetName: 'Lenovo IdeaCentre',
        amount: 848.00,
        description: 'Desktop for Accountant 3',
      },
    ],
  },
  {
    id: '18',
    auditNumber: 1018,
    assetId: '9',
    assetName: 'HP ZBook Studio',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(28),
    amount: 562.50,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-005',
    items: [
      {
        id: '18-1',
        assetId: '9',
        assetName: 'HP ZBook Studio',
        amount: 187.50,
        description: 'Monthly depreciation',
      },
      {
        id: '18-2',
        assetId: '14',
        assetName: 'Dell Inspiron 15',
        amount: 145.83,
        description: 'Monthly depreciation',
      },
      {
        id: '18-3',
        assetId: '15',
        assetName: 'Acer Aspire 5',
        amount: 229.17,
        description: 'Monthly depreciation',
      },
    ],
  },
  {
    id: '19',
    auditNumber: 1019,
    assetId: '38',
    assetName: 'Industrial Printer - Large Format',
    type: 'addition',
    transactionDate: generateDateDaysAgo(29),
    amount: 13500.00,
    currency: 'GBP',
    status: 'pending',
    description: 'Large format printer for design department',
    preparedBy: 'David Lee',
    preparedDate: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'ADD-2024-0301',
    items: [
      {
        id: '19-1',
        assetId: '38',
        assetName: 'Industrial Printer - Large Format',
        amount: 4500.00,
        description: 'Large format printer',
      },
      {
        id: '19-2',
        assetId: '39',
        assetName: 'HP LaserJet Pro',
        amount: 3500.00,
        description: 'Color laser printers (x2)',
      },
      {
        id: '19-3',
        assetId: '40',
        assetName: 'Canon ImageRunner',
        amount: 5500.00,
        description: 'Multifunction copier',
      },
    ],
  },
  {
    id: '20',
    auditNumber: 1020,
    assetId: '10',
    assetName: 'Lenovo ThinkCentre M90',
    type: 'revaluation',
    transactionDate: generateDateDaysAgo(29),
    amount: 2250.00,
    currency: 'GBP',
    status: 'rejected',
    description: 'Revaluation rejected - asset condition below standard',
    preparedBy: 'Emma Wilson',
    preparedDate: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'REV-2024-004',
    items: [
      {
        id: '20-1',
        assetId: '10',
        assetName: 'Lenovo ThinkCentre M90',
        amount: 750.00,
        description: 'Desktop revaluation',
      },
      {
        id: '20-2',
        assetId: '13',
        assetName: 'Lenovo IdeaCentre',
        amount: 680.00,
        description: 'Desktop revaluation',
      },
      {
        id: '20-3',
        assetId: '15',
        assetName: 'Acer Aspire 5',
        amount: 820.00,
        description: 'Laptop revaluation',
      },
    ],
  },
  {
    id: '21',
    auditNumber: 1021,
    assetId: '11',
    assetName: 'Dell Precision 5560',
    type: 'depreciation',
    transactionDate: generateDateDaysAgo(30),
    amount: 437.50,
    currency: 'GBP',
    status: 'posted',
    description: 'Monthly depreciation',
    preparedBy: 'System',
    preparedDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    postedBy: 'Betty Davis',
    postedDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    journalReference: 'DEP-2024-006',
    items: [
      {
        id: '21-1',
        assetId: '11',
        assetName: 'Dell Precision 5560',
        amount: 145.83,
        description: 'Monthly depreciation',
      },
      {
        id: '21-2',
        assetId: '1',
        assetName: 'Dell Latitude 7420 Laptop',
        amount: 104.17,
        description: 'Monthly depreciation',
      },
      {
        id: '21-3',
        assetId: '3',
        assetName: 'Lenovo ThinkPad X1',
        amount: 187.50,
        description: 'Monthly depreciation',
      },
    ],
  },
];

// Helper function to get the next available audit number
const getNextAuditNumber = (): number => {
  const maxAuditNumber = Math.max(...mockTransactions.map(t => t.auditNumber ?? 0));
  return maxAuditNumber + 1;
};

// Export both for use in mock-data.ts
export { mockTransactions, getNextAuditNumber };
